/* ============================================================================
 * Eventos Fiscais & Carta de Correção
 *
 * Portado de base-html `erpone_eventos_fiscais_e_carta_de_corre_o`:
 *   CC-e Generated (30d) | Cancellations Pending | Manifestations
 *   Recent Document Events (Document/Chave, Event Type, Date/Time, SEFAZ
 *   Status, Action) + geração de CC-e.
 *
 * Contrato real consumido (fiscal-documents.dto.ts):
 *   GET  /fiscal/events?days=30&limit=20
 *     → FiscalEventsSnapshotDto { cceGenerated30d, cancellationsPending,
 *       manifestations, totalEvents, recentEvents[] }
 *   POST /fiscal/events/:id/cc-e  (CreateCceDto → GeneratedCceDto)
 *   GET  /fiscal/invoices?status=AUTHORIZED (picker de documento no modal)
 *
 * Tipos de evento reconhecidos (fiscal-events.controller.ts EVENT_TYPES):
 *   CARTA_CORRECAO, CANCELAMENTO, MANIFESTACAO_* — outros valores são exibidos
 *   como vieram da API (String livre).
 *
 * A geração de CC-e registra o evento localmente (sefazIntegrated: false) —
 * não há transmissão SEFAZ: a tela mostra esse estado com honestidade.
 * ========================================================================== */

import { useState } from 'react';
import { useAuth } from '../../../shared/auth/AuthContext';
import { formatDateTime, formatCount } from '../../../shared/utils/format';
import { Badge, Button, LoadingSkeleton } from '@nexora/design-system';
import { useFiscalEvents } from './fiscalDocumentsHooks';
import {
  eventTypeLabel,
  eventTypeIcon,
  shortAccessKey,
} from './fiscalStatusMaps';
import { FISCAL_DOCUMENTS_CSS } from './fiscalDocumentsCss';
import { GenerateCceModal, type CceTarget } from './GenerateCceModal';

export function FiscalEventsPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';

  const [cceOpen, setCceOpen] = useState(false);
  const [cceTarget, setCceTarget] = useState<CceTarget | null>(null);

  const { data, isLoading, error, refetch } = useFiscalEvents(tenantId, 30, 20);

  const snapshot = data;
  const events = snapshot?.recentEvents ?? [];

  const kpis = snapshot
    ? [
        { key: 'cce', label: 'CC-e geradas (30d)', value: snapshot.cceGenerated30d, icon: 'edit_document', tone: 'ok' },
        { key: 'cancel', label: 'Cancelamentos pendentes', value: snapshot.cancellationsPending, icon: 'cancel', tone: 'bad' },
        { key: 'manif', label: 'Manifestações', value: snapshot.manifestations, icon: 'assignment_turned_in', tone: 'info' },
        { key: 'total', label: 'Total de eventos', value: snapshot.totalEvents, icon: 'event_note', tone: 'muted' },
      ]
    : [];

  const openCceFor = (ev: (typeof events)[number]): void => {
    setCceTarget({
      fiscalDocumentId: ev.fiscalDocumentId,
      documentNumber: ev.document?.documentNumber ?? null,
      serie: ev.document?.serie ?? null,
      type: ev.document?.type ?? null,
      accessKey: ev.document?.accessKey ?? null,
    });
    setCceOpen(true);
  };

  const openNewCce = (): void => {
    setCceTarget(null);
    setCceOpen(true);
  };

  const sefazStatus = (ev: (typeof events)[number]): { label: string; variant: 'success' | 'warning' | 'info' | 'neutral' } => {
    if (ev.type === 'CANCELAMENTO' && !ev.protocol) {
      return { label: 'Pendente de confirmação', variant: 'warning' };
    }
    if (ev.protocol) {
      return { label: `Protocolo ${ev.protocol}`, variant: 'success' };
    }
    if (ev.type === 'CARTA_CORRECAO') {
      return { label: 'Registro local (sem envio)', variant: 'info' };
    }
    return { label: 'Sem protocolo', variant: 'neutral' };
  };

  return (
    <div className="fiscal-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Eventos Fiscais & Carta de Correção
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            CC-e, cancelamentos e manifestações registradas nos documentos.
          </p>
        </div>
        <Button variant="primary" onClick={openNewCce}>
          <span className="material-symbols-outlined fiscal-status-icon" aria-hidden="true">
            edit_document
          </span>
          Gerar CC-e
        </Button>
      </div>

      {/* ── KPIs (agregação real do servidor) ── */}
      {kpis.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-md mb-lg">
          {kpis.map((k) => (
            <div key={k.key} className="fiscal-kpi">
              <div className="fiscal-kpi__top">
                <span className="material-symbols-outlined" aria-hidden="true">
                  {k.icon}
                </span>
                <span className="font-label-md text-label-md text-on-surface-variant uppercase">
                  {k.label}
                </span>
              </div>
              <div className={`font-display-lg text-display-lg tabular-nums fiscal-kpi--${k.tone}`}>
                {formatCount(k.value)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Eventos recentes ── */}
      <div className="fiscal-card">
        {isLoading && (
          <div style={{ padding: '1rem' }}>
            <LoadingSkeleton count={6} height="40px" />
          </div>
        )}

        {error && !isLoading && (
          <div className="fiscal-state">
            <p className="fiscal-state__title">Erro ao carregar eventos fiscais</p>
            <p className="fiscal-state__detail">{(error as Error).message}</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !error && events.length === 0 && (
          <div className="fiscal-state">
            <p className="fiscal-state__detail">
              Nenhum evento fiscal registrado nos últimos 30 dias. Eventos de CC-e,
              cancelamento e manifestação aparecerão aqui quando existirem.
            </p>
          </div>
        )}

        {!isLoading && !error && events.length > 0 && (
          <div className="fiscal-table">
            <table>
              <thead>
                <tr>
                  <th>Documento / Chave</th>
                  <th>Tipo de evento</th>
                  <th>Data/Hora</th>
                  <th>Status SEFAZ</th>
                  <th>Motivo</th>
                  <th style={{ textAlign: 'right' }}>Ação</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => {
                  const st = sefazStatus(ev);
                  const docNum = ev.document?.documentNumber
                    ? `${ev.document.documentNumber}${ev.document.serie ? `/${ev.document.serie}` : ''}`
                    : null;
                  return (
                    <tr key={ev.id}>
                      <td>
                        <span className="fiscal-recipient fiscal-mono" style={{ fontWeight: 500 }}>
                          {docNum ?? '—'}
                        </span>
                        <span className="fiscal-recipient-doc">
                          {shortAccessKey(ev.document?.accessKey)}
                        </span>
                      </td>
                      <td>
                        <span className="material-symbols-outlined fiscal-status-icon" aria-hidden="true">
                          {eventTypeIcon(ev.type)}
                        </span>
                        {eventTypeLabel(ev.type)}
                        {ev.sequence > 0 && (
                          <span className="fiscal-recipient-doc">Seq: {ev.sequence}</span>
                        )}
                      </td>
                      <td className="fiscal-nowrap">{formatDateTime(ev.registeredAt)}</td>
                      <td>
                        <Badge variant={st.variant} size="sm">
                          {st.label}
                        </Badge>
                      </td>
                      <td style={{ maxWidth: '280px' }}>{ev.motivo ?? '—'}</td>
                      <td className="fiscal-actions">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openCceFor(ev)}
                          title="Gerar carta de correção para este documento"
                        >
                          Gerar CC-e
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <GenerateCceModal
        isOpen={cceOpen}
        initialTarget={cceTarget}
        onClose={() => setCceOpen(false)}
      />

      <style>{FISCAL_DOCUMENTS_CSS}</style>
    </div>
  );
}